import type { PatchApiV1ThreeLetterCodeClientAdminsClientNewslettersIdBody } from '@fukuri-admins/apis/client/gen/model';
import { fileToUrlByBase64 } from '@fukuri-admins/utils/common/fileToUrlByBase64';
import { PUBLISH_TYPE } from '../../constants';
import type { EditInternalNewsFormValues } from '../schema';

type ExistingAttachment = {
  blobSignedId: string;
  url: string;
};

const resolvePublishAt = (data: EditInternalNewsFormValues): string => {
  if (data.publishMode === PUBLISH_TYPE.IMMEDIATELY) {
    return new Date().toISOString();
  }
  if (data.scheduledDate && data.scheduledTime) {
    const [hours, minutes] = data.scheduledTime.split(':').map(Number);
    const scheduledAt = new Date(data.scheduledDate);
    scheduledAt.setHours(hours, minutes, 0, 0);
    return scheduledAt.toISOString();
  }
  return new Date().toISOString();
};

type Args = {
  formValues: EditInternalNewsFormValues;
  /** 元のメイン画像が存在していたか */
  hadMainImage: boolean;
  /** ユーザーがメイン画像を明示的に削除したか */
  isMainImageRemoved: boolean;
  /** 元の添付ファイル一覧（削除判定用） */
  originalAttachments: ExistingAttachment[];
  /** 削除されずに残っている既存添付ファイル一覧 */
  remainingAttachments: ExistingAttachment[];
  /** 新規アップロード済みファイルの signed_id 一覧 */
  newFileSignedIds: string[];
  /** 公開済み記事かどうか（true の場合は publish_at を送信しない） */
  isPublished: boolean;
};

/**
 * フォーム値から社内報更新 API の JSON リクエストボディを生成する。
 *
 * main_image は Base64 Data URI 形式（新規アップロード）または null（削除）を送信する。
 * 変更がない場合はフィールド自体を省略してサーバー側の保持動作に委ねる。
 *
 * file_signed_ids は全置換方式。残す既存ファイルの blob_signed_id と新規ファイルの signed_id を
 * 合わせて送信する。添付ファイルに変更がない場合は省略してサーバーが既存を保持する。
 */
export const generatePatchRequestBody = async ({
  formValues,
  hadMainImage,
  isMainImageRemoved,
  originalAttachments,
  remainingAttachments,
  newFileSignedIds,
  isPublished,
}: Args): Promise<PatchApiV1ThreeLetterCodeClientAdminsClientNewslettersIdBody> => {
  const clientNewsletter: NonNullable<
    PatchApiV1ThreeLetterCodeClientAdminsClientNewslettersIdBody['client_newsletter']
  > = {
    title: formValues.title,
    body: formValues.body,
  };

  if (!isPublished) {
    clientNewsletter.publish_at = resolvePublishAt(formValues);
  }

  // メイン画像の処理
  if (formValues.mainImage != null) {
    // 新しい画像がアップロードされた場合: Base64 Data URI に変換して送信
    clientNewsletter.main_image = await fileToUrlByBase64(formValues.mainImage);
  } else if (hadMainImage && isMainImageRemoved) {
    // ユーザーが明示的に削除ボタンを押した場合のみ: null を送信
    clientNewsletter.main_image = null;
  }
  // 変更なし（isMainImageRemoved=false かつ新規画像なし）: フィールドを省略してサーバーが既存を保持

  // 添付ファイルの処理
  // API仕様: file_signed_ids 省略=既存保持、配列=指定ファイルのみ残す（全置換）
  // 既存ファイルは blob_signed_id で指定。新規ファイルは blob アップロードの signed_id を使用
  const hasAttachmentChange =
    remainingAttachments.length !== originalAttachments.length || newFileSignedIds.length > 0;

  if (hasAttachmentChange) {
    // 残す既存ファイルの blob_signed_id + 新規ファイルの signed_id を送信
    clientNewsletter.file_signed_ids = [
      ...remainingAttachments.map(a => a.blobSignedId),
      ...newFileSignedIds,
    ];
  }
  // 変更なし: file_signed_ids を省略（サーバーが既存を保持）

  return { client_newsletter: clientNewsletter };
};
